import _ from 'lodash';

import isDefined from 'isDefined';
import cardTypeCheckers from 'types/cardTypeCheckers';

import {
  getEntityDetailsMetricIds,
  isEntityDetailsTable,
} from './buildEntityInformationTableDefinition';
import {
  getMetricListMetricIds,
  isMetricListTable,
} from './buildMetricListDefinition';
import { isRemindersTable } from './buildRemindersDefinition';
import { isRecordsTable } from './buildTableDefinition';
import { getMetricLimit } from './chartDisplayOptions';
import { getKpiListScorecardId, isKpiListTable } from './kpiList';
import { takesNoMetrics } from './legacyGadgetBlocks';
import { getResolvedMetricIds, hasSeriesVariants } from './seriesVariants';
import { getVisType, isMediaVisType, withCurrentVisType } from './visTypes';

// Pure helpers for Composite blocks: a stack of rows, each holding elements
// side by side. Every edit returns new rows, so the settings panel can save
// them through updateCard like any other block setting.

export const MAX_ELEMENTS_PER_ROW = 6;
// Preset element widths and row heights; absent means auto
export const SIZE_PERCENTS = [25, 33, 50, 67, 75];
// Where an element sits in its row and how big it is, from resizing it on
// the canvas. Left behind when it moves to another row (or is copied), which
// lays it out afresh.
export const ELEMENT_LAYOUT_FIELDS = [
  'widthPercent',
  'offsetPercent',
  'heightPercent',
  'offsetTopPercent',
] as const;
export type ElementLayout = Partial<
  Pick<Studio.CompositeElement, (typeof ELEMENT_LAYOUT_FIELDS)[number]>
>;
export const DEFAULT_ELEMENT_VIS_TYPE: Studio.ElementVisType = 'number';

export const buildId = (prefix: string) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

// Number elements (the default) show a headline value; Text shows rich text;
// any other vis type is a chart
export const isNumberElement = (element: Studio.CompositeElement): boolean =>
  (element.visType ?? DEFAULT_ELEMENT_VIS_TYPE) === 'number';

export const isTextElement = (element: Studio.CompositeElement): boolean =>
  element.visType === 'text';

// Plain text from Quill HTML, collapsed to single spaces
export const getTextPreview = (html: string | undefined): string =>
  (html ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

// What an element without metrics shows is still to be chosen: a records
// Table's dataset, a reminders Table's events, an entity details Table's
// rows, a KPI list's KPI List, or a Group vs remainder Line's dataset and
// field
const isMetricFreeElementEmpty = (
  element: Studio.CompositeElement,
): boolean => {
  if (isRecordsTable(element)) {
    return !element.table?.records?.dataType;
  }
  if (isRemindersTable(element)) {
    return (element.table?.reminders?.events ?? []).length === 0;
  }
  if (isEntityDetailsTable(element)) {
    return (element.table?.entityDetails?.rows ?? []).length === 0;
  }
  if (isMetricListTable(element)) {
    return getMetricListMetricIds(element).length === 0;
  }
  if (isKpiListTable(element)) {
    return !getKpiListScorecardId(element);
  }
  const settings = element.line?.groupVsRemainder;
  return !settings || !settings.dataset || !settings.field;
};

// Text elements are empty without text, those without metrics (records,
// reminders, entity details, Group vs remainder) until they're set up,
// everything else
// without metrics
export const isEmptyElement = (element: Studio.CompositeElement): boolean =>
  isTextElement(element)
    ? getTextPreview(element.text) === ''
    : takesNoMetrics(element)
      ? isMetricFreeElementEmpty(element)
      : (element.metricIds ?? []).length === 0;

export const buildElement = (
  element: Omit<Studio.CompositeElement, 'id'> = {},
): Studio.CompositeElement => ({ id: buildId('element'), ...element });

export const buildRow = ({
  elements,
  hasDividers,
}: {
  elements: Studio.CompositeElement[];
  hasDividers?: boolean;
}): Studio.CompositeRow => ({
  id: buildId('row'),
  elements,
  ...(hasDividers ? { hasDividers } : {}),
});

const numberElement = ({
  metricId,
  size,
}: {
  metricId?: string;
  size: Studio.CompositeElement['size'];
}): Studio.CompositeElement =>
  buildElement({
    visType: 'number',
    size,
    ...(metricId ? { metricIds: [metricId] } : {}),
  });

// Turning an existing block into a Composite keeps its metrics: the first two
// become headline numbers over a trend of the primary, the rest a footer.
export const rowsFromMetrics = ({
  metricIds,
}: {
  metricIds: string[];
}): Studio.CompositeRow[] => {
  const [primary, second, ...rest] = metricIds;
  // Without any, it starts empty, as a new Composite does
  if (!primary) {
    return [];
  }
  return [
    buildRow({
      elements: [primary, second]
        .filter(isDefined)
        .map((metricId) => numberElement({ metricId, size: 'large' })),
    }),
    buildRow({
      elements: [buildElement({ visType: 'line', metricIds: [primary] })],
    }),
    ...(rest.length > 0
      ? [
          buildRow({
            elements: rest
              .slice(0, MAX_ELEMENTS_PER_ROW)
              .map((metricId) => numberElement({ metricId, size: 'medium' })),
            hasDividers: true,
          }),
        ]
      : []),
  ];
};

// Elements saved as a 'kpi' (single metricId) or a 'chart' become Number and
// chart vis types
export const migrateStoredElement = ({
  kind,
  metricId,
  ...element
}: Studio.StoredCompositeElement): Studio.CompositeElement =>
  kind === 'kpi'
    ? {
        ...element,
        visType: 'number',
        metricIds: metricId ? [metricId] : element.metricIds,
      }
    : withCurrentVisType(element);

// A copy of a block's content with fresh row and element ids, so a copy of a
// Composite never shares ids with the block it came from
export const cloneCardContent = (
  content: Omit<Studio.Card, 'id' | 'layout'>,
): Omit<Studio.Card, 'id' | 'layout'> => {
  const copy = _.cloneDeep(content);
  return copy.rows
    ? {
        ...copy,
        rows: copy.rows.map((row) => ({
          ...row,
          id: buildId('row'),
          elements: row.elements.map((element) => ({
            ...element,
            id: buildId('element'),
          })),
        })),
      }
    : copy;
};

// The metrics it draws: a chart's series variants as the metrics they ask
// about (see seriesVariants), and an entity details Table's rows'
export const getElementMetricIds = (
  element: Studio.CompositeElement,
): string[] =>
  _.uniq([
    ...getResolvedMetricIds(element),
    ...(isNumberElement(element) && element.denominatorMetricId
      ? [element.denominatorMetricId]
      : []),
    ...getEntityDetailsMetricIds(element),
    ...getMetricListMetricIds(element),
  ]);

// Every metric a block shows, including those inside a Composite's elements
// and an entity details Table's rows
export const getCardMetricIds = (card: Studio.Card): string[] =>
  _.uniq([
    ...getResolvedMetricIds(card),
    ...(card.denominatorMetricId ? [card.denominatorMetricId] : []),
    ...getEntityDetailsMetricIds(card),
    ...getMetricListMetricIds(card),
    ...(card.visType === 'composite'
      ? (card.rows ?? []).flatMap((row) =>
          row.elements.flatMap(getElementMetricIds),
        )
      : []),
  ]);

// Whether a Composite shows something without metrics: an element that
// lists records (once it has its dataset)
export const hasMetricFreeElement = (card: Studio.Card): boolean =>
  card.visType === 'composite' &&
  (card.rows ?? []).some((row) =>
    row.elements.some((e) => takesNoMetrics(e) && !isEmptyElement(e)),
  );

// Every metric a Composite shows, as stored on a dashboard or report
export const getCompositeMetricIds = (
  composite: Studio.CompositeContent,
): string[] =>
  _.uniq(
    (composite.rows ?? []).flatMap((row) =>
      row.elements.flatMap(getElementMetricIds),
    ),
  );

const isNonEmptyString = (id: string | undefined): id is string => !!id;

// What opens a popup: a chart element, and each row of an entity details
// Table
const getPopupSources = (
  element: Studio.CompositeElement,
): { reportDrillDownId?: string; boardDrillDownId?: string }[] => [
  element,
  ...(isEntityDetailsTable(element)
    ? element.table?.entityDetails?.rows ?? []
    : []),
];

// The popup reports and boards a Composite's elements open (dashboards
// only), for "where is this used as a popup"
export const getCompositePopupTargets = (
  composite: Studio.CompositeContent,
): { reportIds: string[]; boardIds: string[] } => {
  const sources = (composite.rows ?? [])
    .flatMap((row) => row.elements)
    .flatMap(getPopupSources);
  return {
    reportIds: _.uniq(
      sources.map((e) => e.reportDrillDownId).filter(isNonEmptyString),
    ),
    boardIds: _.uniq(
      sources.map((e) => e.boardDrillDownId).filter(isNonEmptyString),
    ),
  };
};

// As above, across every Composite on a canvas
export const getCanvasCompositePopupTargets = (
  cards: CanvasCard.Card[],
): { reportIds: string[]; boardIds: string[] } => {
  const targets = cards
    .filter(cardTypeCheckers.isStudioComposite)
    .map((card) => getCompositePopupTargets(card.content.composite));
  return {
    reportIds: _.uniq(targets.flatMap((t) => t.reportIds)),
    boardIds: _.uniq(targets.flatMap((t) => t.boardIds)),
  };
};

// Text takes the height it needs, like a Number
export const rowHasChart = (row: Studio.CompositeRow): boolean =>
  row.elements.some((e) => !isNumberElement(e) && !isTextElement(e));

export const findElement = ({
  rows,
  elementId,
}: {
  rows: Studio.CompositeRow[];
  elementId: string | undefined;
}): Studio.CompositeElement | undefined =>
  elementId
    ? rows.flatMap((r) => r.elements).find((e) => e.id === elementId)
    : undefined;

export const updateElement = ({
  rows,
  element,
}: {
  rows: Studio.CompositeRow[];
  element: Studio.CompositeElement;
}): Studio.CompositeRow[] =>
  rows.map((row) => ({
    ...row,
    elements: row.elements.map((e) => (e.id === element.id ? element : e)),
  }));

// A Number shows one metric, as do Pies and other charts of one (see
// getMetricLimit), so a new one replaces it; other charts collect them
const withMetric = ({
  element,
  metricId,
}: {
  element: Studio.CompositeElement;
  metricId: string;
}): Studio.CompositeElement => ({
  ...element,
  metricIds:
    isNumberElement(element) || getMetricLimit(element) === 1
      ? [metricId]
      : _.uniq([...(element.metricIds ?? []), metricId]),
});

// Everything but Numbers and Text: charts, Tables, Trends and Gauges
export const isChartElement = (element: Studio.CompositeElement): boolean =>
  !isNumberElement(element) && !isTextElement(element);

// Text and records Tables never take metrics, and filled Numbers and charts
// of one (e.g. a Pie) only when dropped on
const canTakeMetric = (element: Studio.CompositeElement): boolean =>
  (isChartElement(element) &&
    !takesNoMetrics(element) &&
    (isEmptyElement(element) || getMetricLimit(element) !== 1)) ||
  (isNumberElement(element) && isEmptyElement(element));

// Where a metric added to a Composite lands: the element it was dropped on
// (unless it's Text or a records Table; a filled Number's or Pie's metric is
// replaced), else the selected element (if it can take another), else the
// first empty Number, else the first empty chart, else a new Number appended
// to the last Number-only row with room (or a new footer row). Filled Numbers
// and charts of one are only overwritten when dropped on, so adding metrics
// one after another fills the slots in turn. Returns the element it filled.
export const addMetricToComposite = ({
  rows,
  metricId,
  selectedElementId,
  droppedOnElementId,
}: {
  rows: Studio.CompositeRow[];
  metricId: string;
  selectedElementId?: string;
  droppedOnElementId?: string;
}): { rows: Studio.CompositeRow[]; elementId: string } => {
  const elements = rows.flatMap((r) => r.elements);
  const droppedOn = findElement({ rows, elementId: droppedOnElementId });
  const selected = findElement({ rows, elementId: selectedElementId });
  const target =
    (droppedOn && !isTextElement(droppedOn) && !takesNoMetrics(droppedOn)
      ? droppedOn
      : undefined) ??
    (selected && canTakeMetric(selected) ? selected : undefined) ??
    elements.find((e) => isNumberElement(e) && isEmptyElement(e)) ??
    elements.find((e) => canTakeMetric(e) && isEmptyElement(e));
  if (target) {
    return {
      rows: updateElement({
        rows,
        element: withMetric({ element: target, metricId }),
      }),
      elementId: target.id,
    };
  }

  const appended = numberElement({ metricId, size: 'medium' });
  const numberRow = _.findLast(
    rows,
    (r) =>
      r.elements.every(isNumberElement) &&
      r.elements.length < MAX_ELEMENTS_PER_ROW,
  );
  if (numberRow) {
    return {
      rows: rows.map((r) =>
        r.id === numberRow.id
          ? { ...r, elements: [...r.elements, appended] }
          : r,
      ),
      elementId: appended.id,
    };
  }
  return {
    rows: [...rows, buildRow({ elements: [appended], hasDividers: true })],
    elementId: appended.id,
  };
};

// Removing a row's last element removes the row
export const removeElement = ({
  rows,
  elementId,
}: {
  rows: Studio.CompositeRow[];
  elementId: string;
}): Studio.CompositeRow[] =>
  rows
    .map((row) => ({
      ...row,
      elements: row.elements.filter((e) => e.id !== elementId),
    }))
    .filter((row) => row.elements.length > 0);

// A copy of an element, straight after it in its row, or in a row of its own
// below when the row is full. The copy has no set width of its own. The rows
// unchanged when there's no such element.
export const duplicateElement = ({
  rows,
  elementId,
}: {
  rows: Studio.CompositeRow[];
  elementId: string;
}): { rows: Studio.CompositeRow[]; elementId?: string } => {
  const element = findElement({ rows, elementId });
  const row = findRowOfElement({ rows, elementId });
  if (!element || !row) {
    return { rows };
  }
  const copy: Studio.CompositeElement = {
    ..._.cloneDeep(_.omit(element, ELEMENT_LAYOUT_FIELDS)),
    id: buildId('element'),
  };
  if (row.elements.length >= MAX_ELEMENTS_PER_ROW) {
    const at = rows.indexOf(row) + 1;
    return {
      rows: [
        ...rows.slice(0, at),
        buildRow({ elements: [copy] }),
        ...rows.slice(at),
      ],
      elementId: copy.id,
    };
  }
  return {
    rows: rows.map((r) =>
      r.id === row.id
        ? {
            ...r,
            elements: r.elements.flatMap((e) =>
              e.id === elementId ? [e, copy] : [e],
            ),
          }
        : r,
    ),
    elementId: copy.id,
  };
};

// A new element in a row (at its end, or before beforeElementId): empty,
// typed later from the vis type palette or settings, or as given (e.g.
// dropped into the row). Undefined elementId when the row is already full.
export const addElement = ({
  rows,
  rowId,
  element: settings = {},
  beforeElementId,
}: {
  rows: Studio.CompositeRow[];
  rowId: string;
  element?: Omit<Studio.CompositeElement, 'id'>;
  // Where it goes in the row: before this element, else at the end
  beforeElementId?: string;
}): { rows: Studio.CompositeRow[]; elementId?: string } => {
  const row = rows.find((r) => r.id === rowId);
  if (!row || row.elements.length >= MAX_ELEMENTS_PER_ROW) {
    return { rows };
  }
  // A Number is sized like the row's existing Numbers
  const sibling = row.elements.find(isNumberElement);
  const element = buildElement({
    ...(sibling && sibling.size && isNumberElement({ id: '', ...settings })
      ? { size: sibling.size }
      : {}),
    ...settings,
  });
  const at = beforeElementId
    ? row.elements.findIndex((e) => e.id === beforeElementId)
    : -1;
  const elements =
    at === -1
      ? [...row.elements, element]
      : [...row.elements.slice(0, at), element, ...row.elements.slice(at)];
  return {
    rows: rows.map((r) => (r.id === rowId ? { ...r, elements } : r)),
    elementId: element.id,
  };
};

// A new row with one element (empty, or as given), at the end or straight
// after afterRowId
export const addRow = ({
  rows,
  afterRowId,
  element: settings,
}: {
  rows: Studio.CompositeRow[];
  afterRowId?: string;
  element?: Omit<Studio.CompositeElement, 'id'>;
}): { rows: Studio.CompositeRow[]; elementId: string } => {
  const element = buildElement(settings);
  const row = buildRow({ elements: [element] });
  const after = afterRowId ? rows.findIndex((r) => r.id === afterRowId) : -1;
  const at = after === -1 ? rows.length : after + 1;
  return {
    rows: [...rows.slice(0, at), row, ...rows.slice(at)],
    elementId: element.id,
  };
};

// The chart element's metrics that no Number in the block shows yet: what
// addLegendNumbers adds. Empty for split charts, whose series are dimension
// values, not metrics, for charts with series variants, which a Number of
// the plain metric would misrepresent (see compositeLegend), and for Gauges
// and records Tables, which have no legend.
export const getMetricsWithoutNumbers = ({
  rows,
  elementId,
}: {
  rows: Studio.CompositeRow[];
  elementId: string;
}): string[] => {
  const element = findElement({ rows, elementId });
  if (
    !element ||
    isNumberElement(element) ||
    isTextElement(element) ||
    element.visType === 'gauge' ||
    takesNoMetrics(element) ||
    (element.dimensions ?? []).length > 0 ||
    hasSeriesVariants(element)
  ) {
    return [];
  }
  const numberMetricIds = new Set(
    rows
      .flatMap((r) => r.elements)
      .filter(isNumberElement)
      .flatMap((e) => e.metricIds ?? []),
  );
  return _.uniq(element.metricIds ?? []).filter(
    (metricId) => !numberMetricIds.has(metricId),
  );
};

// Numbers for the chart element's metrics that don't have one, so they
// stand in for its legend: added to the row beneath it when that's a row
// of Numbers with room, else in a new divided row beneath it. A row holds
// MAX_ELEMENTS_PER_ROW, so metrics past that are left out.
export const addLegendNumbers = ({
  rows,
  elementId,
}: {
  rows: Studio.CompositeRow[];
  elementId: string;
}): Studio.CompositeRow[] => {
  const metricIds = getMetricsWithoutNumbers({ rows, elementId });
  const chartRow = findRowOfElement({ rows, elementId });
  if (metricIds.length === 0 || !chartRow) {
    return rows;
  }
  const at = rows.findIndex((r) => r.id === chartRow.id) + 1;
  const below = rows[at];
  const isNumbersRow =
    !!below &&
    below.elements.length > 0 &&
    below.elements.every(isNumberElement) &&
    below.elements.length < MAX_ELEMENTS_PER_ROW;
  if (isNumbersRow) {
    // Sized like the Numbers already there
    const size = below.elements[0].size ?? 'medium';
    const room = MAX_ELEMENTS_PER_ROW - below.elements.length;
    return rows.map((r) =>
      r.id === below.id
        ? {
            ...r,
            elements: [
              ...r.elements,
              ...metricIds
                .slice(0, room)
                .map((metricId) => numberElement({ metricId, size })),
            ],
          }
        : r,
    );
  }
  const row = buildRow({
    elements: metricIds
      .slice(0, MAX_ELEMENTS_PER_ROW)
      .map((metricId) => numberElement({ metricId, size: 'medium' })),
    hasDividers: true,
  });
  return [...rows.slice(0, at), row, ...rows.slice(at)];
};

export const removeRow = ({
  rows,
  rowId,
}: {
  rows: Studio.CompositeRow[];
  rowId: string;
}): Studio.CompositeRow[] => rows.filter((r) => r.id !== rowId);

export const moveRow = ({
  rows,
  rowId,
  direction,
}: {
  rows: Studio.CompositeRow[];
  rowId: string;
  direction: 'up' | 'down';
}): Studio.CompositeRow[] => {
  const from = rows.findIndex((r) => r.id === rowId);
  const to = direction === 'up' ? from - 1 : from + 1;
  if (from === -1 || to < 0 || to >= rows.length) {
    return rows;
  }
  const next = [...rows];
  [next[from], next[to]] = [next[to], next[from]];
  return next;
};

// Moves a row before another one, or to the bottom. The rows unchanged when
// there's no such row.
export const moveRowBefore = ({
  rows,
  rowId,
  beforeRowId,
}: {
  rows: Studio.CompositeRow[];
  rowId: string;
  beforeRowId?: string;
}): Studio.CompositeRow[] => {
  const row = rows.find((r) => r.id === rowId);
  if (!row || beforeRowId === rowId) {
    return rows;
  }
  const others = rows.filter((r) => r.id !== rowId);
  const at = beforeRowId
    ? others.findIndex((r) => r.id === beforeRowId)
    : others.length;
  if (at === -1) {
    return rows;
  }
  return [...others.slice(0, at), row, ...others.slice(at)];
};

// Moves an element into a row: before another element of it, or at its end.
// The row it leaves goes if emptied. Moved to another row, it shares out that
// row's width afresh. Undefined when that row is full; the rows unchanged when
// there's no such element or row.
export const moveElement = ({
  rows,
  elementId,
  toRowId,
  beforeElementId,
}: {
  rows: Studio.CompositeRow[];
  elementId: string;
  toRowId: string;
  beforeElementId?: string;
}): Studio.CompositeRow[] | undefined => {
  const element = findElement({ rows, elementId });
  const fromRow = findRowOfElement({ rows, elementId });
  if (!element || !fromRow || beforeElementId === elementId) {
    return rows;
  }
  const moving =
    fromRow.id === toRowId ? element : _.omit(element, ELEMENT_LAYOUT_FIELDS);
  const lifted = removeElement({ rows, elementId });
  const target = lifted.find((r) => r.id === toRowId);
  if (!target) {
    return rows;
  }
  if (target.elements.length >= MAX_ELEMENTS_PER_ROW) {
    return undefined;
  }
  const at = beforeElementId
    ? target.elements.findIndex((e) => e.id === beforeElementId)
    : -1;
  const elements =
    at === -1
      ? [...target.elements, moving]
      : [...target.elements.slice(0, at), moving, ...target.elements.slice(at)];
  return lifted.map((r) => (r.id === target.id ? { ...r, elements } : r));
};

export const setRowDividers = ({
  rows,
  rowId,
  hasDividers,
}: {
  rows: Studio.CompositeRow[];
  rowId: string;
  hasDividers: boolean;
}): Studio.CompositeRow[] =>
  rows.map((r) => (r.id === rowId ? { ...r, hasDividers } : r));

export const findRowOfElement = ({
  rows,
  elementId,
}: {
  rows: Studio.CompositeRow[];
  elementId: string | undefined;
}): Studio.CompositeRow | undefined =>
  elementId
    ? rows.find((r) => r.elements.some((e) => e.id === elementId))
    : undefined;

// Undefined heightPercent returns the row to auto
export const setRowHeight = ({
  rows,
  rowId,
  heightPercent,
}: {
  rows: Studio.CompositeRow[];
  rowId: string;
  heightPercent: number | undefined;
}): Studio.CompositeRow[] =>
  rows.map((r) =>
    r.id === rowId
      ? heightPercent === undefined
        ? _.omit(r, 'heightPercent')
        : { ...r, heightPercent }
      : r,
  );

// How a row takes its share of the block's height:
// - 'share': the height set on it, exactly (scaled down if the set heights
//   add up to more than the block)
// - 'fill': a chart row without a set height takes what the others leave
// - 'content': Numbers and Text take the height they need
// - 'grow': ...unless no chart row is left to fill, when they share the
//   rest instead of leaving it empty
export type RowSizing =
  | { mode: 'share'; percent: number }
  | { mode: 'fill' }
  | { mode: 'content' }
  | { mode: 'grow' };

// A row's share of the block: the height set on it, or what its tallest
// sized element takes (with the space above it), whichever is more
export const getRowHeightPercent = (
  row: Studio.CompositeRow,
): number | undefined => {
  const elementHeights = row.elements.flatMap((e) =>
    e.heightPercent === undefined
      ? []
      : [e.heightPercent + (e.offsetTopPercent ?? 0)],
  );
  return elementHeights.length === 0
    ? row.heightPercent
    : Math.max(row.heightPercent ?? 0, ...elementHeights);
};

// How much the set heights are scaled down by, should they add up to more
// than the block
export const getRowsScale = (rows: Studio.CompositeRow[]): number => {
  const setTotal = _.sumBy(rows, (r) => getRowHeightPercent(r) ?? 0);
  return setTotal > 100 ? 100 / setTotal : 1;
};

export const getRowSizing = (rows: Studio.CompositeRow[]): RowSizing[] => {
  const scale = getRowsScale(rows);
  const hasFillRow = rows.some(
    (r) => getRowHeightPercent(r) === undefined && rowHasChart(r),
  );
  return rows.map((r): RowSizing => {
    const heightPercent = getRowHeightPercent(r);
    if (heightPercent !== undefined) {
      return { mode: 'share', percent: heightPercent * scale };
    }
    if (rowHasChart(r)) {
      return { mode: 'fill' };
    }
    return { mode: hasFillRow ? 'content' : 'grow' };
  });
};

// Every set height (rows' and their elements', with the space above them)
// scaled by factor, to a decimal place: kept as tall as the block grows
export const scaleRowHeights = ({
  rows,
  factor,
}: {
  rows: Studio.CompositeRow[];
  factor: number;
}): Studio.CompositeRow[] => {
  const scale = (n: number | undefined) =>
    n === undefined ? undefined : Math.round(n * factor * 10) / 10;
  return rows.map((r) => ({
    ...r,
    ...(r.heightPercent === undefined
      ? {}
      : { heightPercent: scale(r.heightPercent) }),
    elements: r.elements.map((e) => ({
      ...e,
      ...(e.heightPercent === undefined
        ? {}
        : { heightPercent: scale(e.heightPercent) }),
      ...(e.offsetTopPercent === undefined
        ? {}
        : { offsetTopPercent: scale(e.offsetTopPercent) }),
    })),
  }));
};

// Rows resized taller than the block has room for: how many grid rows the
// block grows by to fit them as they were dragged, and the rows with their
// set heights scaled down to stay as tall in it. A set height is a share of
// the rows' height (percentPx each, as measured when the drag started);
// other rows keep the height they had (rowHeightsPx, by row). No growth
// when they fit.
export const growRowsToFit = ({
  rows,
  rowsHeight,
  percentPx,
  gridRowPx,
  rowHeightsPx,
}: {
  rows: Studio.CompositeRow[];
  // The rows' height in the block (px)
  rowsHeight: number;
  percentPx: number;
  // A grid row's height on the canvas (px)
  gridRowPx: number;
  rowHeightsPx: Record<string, number>;
}): { rows: Studio.CompositeRow[]; gridRows: number } => {
  const neededPx = _.sumBy(rows, (r) => {
    const heightPercent = getRowHeightPercent(r);
    return heightPercent === undefined
      ? rowHeightsPx[r.id] ?? 0
      : heightPercent * percentPx;
  });
  if (
    rowsHeight <= 0 ||
    percentPx <= 0 ||
    gridRowPx <= 0 ||
    // Within a pixel, rounding aside
    neededPx <= rowsHeight + 1
  ) {
    return { rows, gridRows: 0 };
  }
  const gridRows = Math.ceil((neededPx - rowsHeight) / gridRowPx);
  return {
    rows: scaleRowHeights({
      rows,
      factor: (percentPx * 100) / (rowsHeight + gridRows * gridRowPx),
    }),
    gridRows,
  };
};

// A row dragged taller or shorter by its bottom edge (deltaPx), snapped to
// stepPx: the block grows or shrinks by as much (gridRows, whole grid rows),
// so the rest keep their heights and move with it. The row is at least
// minPx, its children's set heights kept within it. The block's measures
// are as the drag started (see growRowsToFit).
export const resizeRowHeight = ({
  rows,
  rowId,
  deltaPx,
  stepPx,
  minPx,
  rowsHeight,
  percentPx,
  gridRowPx,
  rowHeightsPx,
}: {
  rows: Studio.CompositeRow[];
  rowId: string;
  deltaPx: number;
  stepPx: number;
  minPx: number;
  rowsHeight: number;
  percentPx: number;
  gridRowPx: number;
  rowHeightsPx: Record<string, number>;
}): { rows: Studio.CompositeRow[]; gridRows: number } => {
  const startPx = rowHeightsPx[rowId];
  if (
    startPx === undefined ||
    rowsHeight <= 0 ||
    percentPx <= 0 ||
    gridRowPx <= 0 ||
    stepPx <= 0
  ) {
    return { rows, gridRows: 0 };
  }
  const snapped = Math.round(deltaPx / stepPx) * stepPx;
  const gridRows = Math.round(
    (Math.max(minPx, startPx + snapped) - startPx) / gridRowPx,
  );
  if (gridRows === 0) {
    return { rows, gridRows: 0 };
  }
  const newRowsHeight = rowsHeight + gridRows * gridRowPx;
  const rowPercent =
    Math.round(((startPx + gridRows * gridRowPx) / newRowsHeight) * 1000) / 10;
  const scaled = scaleRowHeights({
    rows,
    factor: (percentPx * 100) / newRowsHeight,
  });
  return {
    rows: scaled.map((r) =>
      r.id === rowId
        ? {
            ...r,
            heightPercent: rowPercent,
            elements: r.elements.map((e) => {
              if (e.heightPercent === undefined) {
                return e;
              }
              // Moved up, then shortened, to stay within the row
              const offset = Math.min(e.offsetTopPercent ?? 0, rowPercent);
              const height = Math.min(e.heightPercent, rowPercent - offset);
              return height > 0
                ? {
                    ..._.omit(e, 'offsetTopPercent'),
                    heightPercent: height,
                    ...(offset > 0 ? { offsetTopPercent: offset } : {}),
                  }
                : _.omit(e, ['heightPercent', 'offsetTopPercent']);
            }),
          }
        : r,
    ),
    gridRows,
  };
};

// Along one axis, an element's size and the free space before it (in % of
// its row's width, or of the block's height)
export type Span = { offset: number; size: number };

// Resizing an element from one of its edges, by delta (%), one at a time:
// the rest never move or resize. From its end it grows into the free space
// after it (the next one's offset, or what's left of the total), and
// shrinking leaves that space, the next one staying put. From its start it
// grows into the space before it, and shrinking leaves that. It keeps at
// least min. Returns every span, to a decimal place.
export const resizeSpan = ({
  spans,
  index,
  edge,
  delta,
  min,
  total,
}: {
  spans: Span[];
  index: number;
  edge: 'start' | 'end';
  delta: number;
  min: number;
  total: number;
}): Span[] => {
  const span = spans[index];
  if (!span) {
    return spans;
  }
  const next = spans[index + 1];
  const free =
    edge === 'start'
      ? span.offset
      : next
        ? next.offset
        : total - _.sumBy(spans, (s) => s.offset + s.size);
  // How much it grows: down to its least, up to the free space
  const grow = _.clamp(
    edge === 'start' ? -delta : delta,
    Math.min(min - span.size, 0),
    Math.max(free, 0),
  );
  const round = (n: number) => Math.round(n * 10) / 10;
  return spans.map((s, i) => {
    if (i === index) {
      return {
        offset: round(edge === 'start' ? s.offset - grow : s.offset),
        size: round(s.size + grow),
      };
    }
    if (i === index + 1 && edge === 'end') {
      return { offset: round(s.offset - grow), size: s.size };
    }
    return s;
  });
};

// Elements given their layouts (see ElementLayout), by id. An undefined
// field returns that to auto; a zero offset is no offset.
export const setElementLayouts = ({
  rows,
  layouts,
}: {
  rows: Studio.CompositeRow[];
  layouts: Record<string, ElementLayout>;
}): Studio.CompositeRow[] =>
  rows.map((r) =>
    r.elements.some((e) => layouts[e.id])
      ? {
          ...r,
          elements: r.elements.map((e) => {
            const layout = layouts[e.id];
            if (!layout) {
              return e;
            }
            const keys = Object.keys(layout) as (keyof ElementLayout)[];
            const cleared = keys.filter((key) => {
              const value = layout[key];
              return (
                value === undefined ||
                (value === 0 &&
                  (key === 'offsetPercent' || key === 'offsetTopPercent'))
              );
            });
            return {
              ..._.omit(e, cleared),
              ..._.omit(layout, cleared),
            };
          }),
        }
      : r,
  );

// Whether a row can be taller than what's in it (its Numbers then sit in
// its middle)
export const isRowStretched = (sizing: RowSizing) =>
  sizing.mode === 'share' || sizing.mode === 'grow';

// The least width (px) a Number in a plain row gets before the row stops
// sizing its Numbers to their content and splits evenly instead
export const NUMBER_NATURAL_MIN_WIDTH = 180;

// Whether a plain row is too narrow for its Numbers to sit at their natural
// widths. Sized by content, a Number shrinking to fit also shrinks its own
// cell, so in a tight row it never catches up and is cut off. Not when the
// row hasn't been laid out (width 0).
export const isRowTight = ({
  rowWidth,
  numberCount,
}: {
  rowWidth: number;
  numberCount: number;
}): boolean =>
  rowWidth > 0 &&
  numberCount > 0 &&
  rowWidth / numberCount < NUMBER_NATURAL_MIN_WIDTH;

// The scale every Number in a row is drawn at: the least any of them needs
// (each as a share of its own largest size) so the row shrinks together,
// or 1 when none has had to shrink yet
export const getRowScale = (scales: (number | undefined)[]): number =>
  Math.min(1, ...scales.filter(isDefined));

// Percentage change from the comparison period. Undefined when there's no
// earlier value to compare against.
export const getDeltaPercent = ({
  current,
  previous,
}: {
  current: number | undefined;
  previous: number | undefined;
}): number | undefined =>
  current === undefined || previous === undefined || previous === 0
    ? undefined
    : ((current - previous) / Math.abs(previous)) * 100;

export const getRatioPercent = ({
  numerator,
  denominator,
}: {
  numerator: number | undefined;
  denominator: number | undefined;
}): number | undefined =>
  numerator === undefined || denominator === undefined || denominator === 0
    ? undefined
    : (numerator / denominator) * 100;

export const isTargetMet = ({
  value,
  target,
}: {
  value: number | undefined;
  target: NonNullable<Studio.CompositeElement['target']>;
}): boolean | undefined =>
  value === undefined
    ? undefined
    : target.operator === 'gte'
      ? value >= target.value
      : value <= target.value;

// Merging blocks into a Composite, and detaching elements back out as blocks.
// Blocks and elements share most of their settings; what only one of them has
// (a block's name and gadget settings, a Number element's size and target) is
// mapped across where there's an equivalent and dropped where there isn't.

const SHARED_CONTENT_KEYS = [
  'metricIds',
  'seriesVariants',
  'dimensions',
  'dimensionOptions',
  'trend',
  'gauge',
  'line',
  'drillDowns',
  'reportDrillDownId',
  'boardDrillDownId',
  'text',
  'forecast',
  'table',
  'chart',
  'chartColors',
  'subtext',
  'denominatorMetricId',
  'denominatorLabel',
  'showProgressBar',
] as const;

// Why a block can't become an element, or undefined when it can. Every vis
// type can be an element; a Composite's rows are merged in. A Classic
// gadget, which needs a gadget of its own, can't, nor can media, saved as
// cards of their own.
export const getMergeBlocker = (card: Studio.Card): string | undefined => {
  if (card.visType === 'classic') {
    return `Classic gadget${card.name ? ` "${card.name}"` : ''} can't go in a Grouped gadget`;
  }
  if (isMediaVisType(card.visType)) {
    return `${getVisType(card.visType).label} gadgets can't go in a Grouped gadget`;
  }
  return undefined;
};

const withoutUndefined = <T extends object>(value: T): T =>
  _.omitBy(value, _.isUndefined) as T;

const nonEmpty = <T>(list: T[]): T[] | undefined =>
  list.length > 0 ? list : undefined;

// A block as an element: its name labels it, its header icon leads a
// Number's label, and a chart keeps its header (titled with the name) unless
// it was hidden. Elements have no gadget, so gadget settings are dropped, and
// a comparison keeps only what it compares against.
export const cardToElement = (card: Studio.Card): Studio.CompositeElement => {
  const visType = (card.visType ??
    DEFAULT_ELEMENT_VIS_TYPE) as Studio.ElementVisType;
  const isChart = isChartElement({ id: card.id, visType });
  return buildElement(
    withoutUndefined({
      ..._.pick(card, SHARED_CONTENT_KEYS),
      visType,
      dateSettings: card.dateSettings,
      filters: card.filters,
      label: card.name,
      icon: visType === 'number' ? card.headerIcon : undefined,
      isHeaderShown: isChart && !card.isHeaderHidden ? true : undefined,
      comparison: card.comparison
        ? { compareType: card.comparison.compareType }
        : undefined,
    }),
  );
};

// An element as a block of its own, keeping what it inherited from its
// Composite: the block's filters (ahead of its own) and the block's dates
// unless it has its own. A chart's header shows only if it did in the
// element.
export const elementToCard = ({
  element,
  block,
}: {
  element: Studio.CompositeElement;
  block: Studio.Card;
}): Omit<Studio.Card, 'id' | 'layout'> =>
  withoutUndefined({
    ..._.cloneDeep(_.pick(element, SHARED_CONTENT_KEYS)),
    visType: element.visType ?? DEFAULT_ELEMENT_VIS_TYPE,
    dateSettings: element.dateSettings ?? block.dateSettings,
    filters: nonEmpty([...(block.filters ?? []), ...(element.filters ?? [])]),
    name: element.label,
    headerIcon: element.icon,
    isHeaderHidden:
      isChartElement(element) && !element.isHeaderShown ? true : undefined,
    accent: block.accent,
    comparison: element.comparison
      ? { compareType: element.comparison.compareType }
      : undefined,
  });

// A Composite's rows with fresh ids, its block's filters and dates pushed
// down onto each element, so they still apply once the rows sit in another
// block
const rowsWithBlockScope = (card: Studio.Card): Studio.CompositeRow[] =>
  (card.rows ?? []).map((row) => ({
    ..._.cloneDeep(row),
    id: buildId('row'),
    elements: row.elements.map((element) =>
      withoutUndefined({
        ..._.cloneDeep(element),
        id: buildId('element'),
        dateSettings: element.dateSettings ?? card.dateSettings,
        filters: nonEmpty([
          ...(card.filters ?? []),
          ...(element.filters ?? []),
        ]),
      }),
    ),
  }));

// The rows of a Composite merged from blocks, following where they sat:
// blocks side by side share a row, left to right, wrapping past
// MAX_ELEMENTS_PER_ROW. A block is beside the others when it starts above
// the bottom of the shortest of them, so a block beside a tall one but
// below a short one starts the next row. A Composite's own rows go in where
// it sat.
export const mergeCardsToRows = (
  cards: Studio.Card[],
): Studio.CompositeRow[] => {
  const bands: Studio.Card[][] = [];
  let bandBottom = Infinity;
  _.sortBy(cards, [(c) => c.layout.y, (c) => c.layout.x]).forEach((card) => {
    const band = _.last(bands);
    const bottom = card.layout.y + card.layout.h;
    if (band && card.layout.y < bandBottom) {
      band.push(card);
      bandBottom = Math.min(bandBottom, bottom);
    } else {
      bands.push([card]);
      bandBottom = bottom;
    }
  });

  return bands.flatMap((band) => {
    const rows: Studio.CompositeRow[] = [];
    let pending: Studio.CompositeElement[] = [];
    const flush = () => {
      _.chunk(pending, MAX_ELEMENTS_PER_ROW).forEach((elements) =>
        rows.push(buildRow({ elements })),
      );
      pending = [];
    };
    _.sortBy(band, (c) => c.layout.x).forEach((card) => {
      if (card.visType === 'composite') {
        flush();
        rows.push(...rowsWithBlockScope(card));
      } else {
        pending.push(cardToElement(card));
      }
    });
    flush();
    return rows;
  });
};

// The smallest layout covering every block
export const getBoundingLayout = (
  layouts: Studio.CardLayout[],
): Studio.CardLayout => {
  const x = Math.min(...layouts.map((l) => l.x));
  const y = Math.min(...layouts.map((l) => l.y));
  return {
    x,
    y,
    w: Math.max(...layouts.map((l) => l.x + l.w)) - x,
    h: Math.max(...layouts.map((l) => l.y + l.h)) - y,
  };
};

// A row of a Composite as a Composite of its own, with the block's look,
// dates and filters. Fresh ids, so it shares none with the block.
export const rowToCompositeCard = ({
  row,
  block,
}: {
  row: Studio.CompositeRow;
  block: Studio.Card;
}): Omit<Studio.Card, 'id' | 'layout'> =>
  withoutUndefined({
    visType: 'composite' as const,
    accent: block.accent,
    dateSettings: block.dateSettings,
    filters: block.filters,
    rows: [
      {
        ..._.cloneDeep(_.omit(row, 'heightPercent')),
        id: buildId('row'),
        elements: row.elements.map((element) => ({
          ..._.cloneDeep(element),
          id: buildId('element'),
        })),
      },
    ],
  });
